'use client'

import React, { useActionState, useRef } from 'react'
import { submitCandidature, type CandidatureFormState } from './actions'

interface Props {
  postes: string[]
}

const inputClass =
  'w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-text-main placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors text-sm'
const labelClass = 'block text-sm font-medium text-text-main mb-1.5'
const font = { fontFamily: 'Poppins, sans-serif' } as const

export default function CandidatureForm({ postes }: Props) {
  const [state, formAction, pending] = useActionState<CandidatureFormState, FormData>(
    submitCandidature,
    { success: false },
  )
  const fileInputRef = useRef<HTMLInputElement>(null)

  if (state.success) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
        </div>
        <h3 className="text-xl font-bold text-text-main mb-2" style={font}>Candidature envoyée !</h3>
        <p className="text-text-muted" style={font}>Merci pour votre candidature. Notre équipe RH vous contactera prochainement.</p>
      </div>
    )
  }

  return (
    <form action={formAction} className="space-y-5" aria-label="Formulaire de candidature" encType="multipart/form-data">
      {state.error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5 text-sm text-red-700" style={font}>
          {state.error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="prenom" className={labelClass} style={font}>Prénom <span className="text-primary" aria-hidden="true">*</span></label>
          <input type="text" id="prenom" name="prenom" required placeholder="Marie" className={inputClass} style={font} />
        </div>
        <div>
          <label htmlFor="nom" className={labelClass} style={font}>Nom <span className="text-primary" aria-hidden="true">*</span></label>
          <input type="text" id="nom" name="nom" required placeholder="Dupont" className={inputClass} style={font} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="email-candidature" className={labelClass} style={font}>Email <span className="text-primary" aria-hidden="true">*</span></label>
          <input type="email" id="email-candidature" name="email" required placeholder="marie.dupont@email.com" className={inputClass} style={font} />
        </div>
        <div>
          <label htmlFor="telephone-candidature" className={labelClass} style={font}>Téléphone</label>
          <input type="tel" id="telephone-candidature" name="telephone" placeholder="+32 4XX XX XX XX" className={inputClass} style={font} />
        </div>
      </div>

      <div>
        <label htmlFor="typePoste" className={labelClass} style={font}>Type de poste <span className="text-primary" aria-hidden="true">*</span></label>
        <select id="typePoste" name="typePoste" required defaultValue="" className={inputClass} style={font}>
          <option value="" disabled>Sélectionnez le type de poste</option>
          <option value="Prestataire de soins">Prestataire de soins (médical / paramédical)</option>
          <option value="Administratif">Poste administratif</option>
        </select>
      </div>

      <div>
        <label htmlFor="poste" className={labelClass} style={font}>Poste souhaité <span className="text-primary" aria-hidden="true">*</span></label>
        <select id="poste" name="poste" required className={inputClass} style={font}>
          <option value="">Sélectionnez un poste</option>
          {postes.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
          <option value="Candidature spontanée">Candidature spontanée</option>
        </select>
      </div>

      <div>
        <label htmlFor="motivation" className={labelClass} style={font}>Lettre de motivation <span className="text-primary" aria-hidden="true">*</span></label>
        <textarea id="motivation" name="motivation" required rows={5} placeholder="Présentez-vous et expliquez votre motivation à rejoindre CMMC CDP..." className={`${inputClass} resize-none`} style={font} />
      </div>

      <div>
        <label htmlFor="cv" className={labelClass} style={font}>CV (PDF, max 5 Mo)</label>
        <div
          className="w-full px-4 py-6 rounded-xl border-2 border-dashed border-gray-200 bg-white text-center hover:border-primary/40 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-2" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
          <p className="text-sm text-text-muted" style={font}>Glissez votre CV ici ou <span className="text-primary font-medium">parcourez vos fichiers</span></p>
          <input
            ref={fileInputRef}
            type="file"
            id="cv"
            name="cv"
            accept=".pdf,.doc,.docx"
            className="sr-only"
            aria-label="Télécharger votre CV"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={pending}
        className="w-full bg-primary text-white font-semibold py-3.5 rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-60"
        style={font}
      >
        {pending ? (
          <>
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            Envoi en cours…
          </>
        ) : (
          <>
            Envoyer ma candidature
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
          </>
        )}
      </button>
    </form>
  )
}
